/**
 * @file PortalTemplatesList.js
 * @description This page displays a list of templates with their respective details such as name, description, and scope.
 */

import '../page-list/PageList.css';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContentType } from '../helpers/getContentType';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';

import {
    createPortalPage,
    deletePortalPage,
    queryPortalPages,
    readPortalPage,
} from '../../services/portalPageService';
import { SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/AuthContext';
import { getAgencies } from '../../services/systemService';
import { getClient } from '../../services/clientService';
import { getNameForDuplicate } from '../../utils/utils';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ClientTemplateScope = 3;

const PortalTemplatesList = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const { showModal } = useContext(ModalContext);
    const [isLoading, setIsLoading] = useState(true);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const { hasAttribute } = useAuthContext();

    useEffect(() => {
        loadTemplates();
        fetchAgencies();
    }, []);

    const fetchAgencies = async () => {
        try {
            const fetchedAgencies = await getAgencies();
            setAgencies(fetchedAgencies.viewModels || []);
        } catch (error) {
            Toast.error(getText('msg_27ded7882fa5'));
        }
    };

    async function loadTemplates() {
        let fetchedTemplates = null;
        try {
            fetchedTemplates = await queryPortalPages(true, null, true);
        } catch (e) {
            Toast.error(getText('msg_6f6ffe8437ab'));
        } finally {
            setIsLoading(false);

            const vm = fetchedTemplates?.viewModels;
            let vmTemplates = [];

            if (Array.isArray(vm)) {
                if (vm.length > 0 && Array.isArray(vm[0])) {
                    vmTemplates = vm[0];
                } else {
                    vmTemplates = vm;
                }
            } else {
                vmTemplates = [];
            }

            const activeTemplates = vmTemplates.filter(t => t && t.status == 1); // == to accept '1' or 1
            setTemplates(activeTemplates);
        }
    }

    const getScopeLabel = template => {
        if (!template) return '';

        if ('tenantId' in template && template.tenantId !== null) {
            return 'Client';
        }

        if (
            'agencyId' in template &&
            template.agencyId !== null &&
            template.agencyId !== undefined
        ) {
            return (
                agencies.find(a => a.id === template.agencyId)?.name ||
                (template.agencyId === 0 ? 'Global' : template.agencyId)
            );
        }

        // fallback for older data
        if ('tenantId' in template && template.tenantId === null)
            return 'Global';
        return 'Client';
    };

    const sortedTemplates = useMemo(() => {
        return [...templates].sort((a, b) => {
            let aValue;
            let bValue;
            if (sortConfig.key === 'templateScope') {
                aValue = getScopeLabel(a);
                bValue = getScopeLabel(b);
            } else if (sortConfig.key === 'type') {
                aValue = getContentType(a[sortConfig.key]);
                bValue = getContentType(b[sortConfig.key]);
            } else {
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
            }

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [templates, sortConfig, agencies]);

    let handleClickName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'name',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'name',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    let handleClickContentType = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'type',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'type',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    let handleClickScope = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'templateScope',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'templateScope',
                direction: SortDirections.ASCENDING,
            });
        }
    };

    const handlerRowClicked = id => {
        let newRowsClicked = rowsClicked;
        if (!rowsClicked.includes(id)) {
            newRowsClicked.push(id);
        } else {
            newRowsClicked.splice(newRowsClicked.indexOf(id), 1);
        }
        setRowsClicked([...newRowsClicked]);
    };

    const deletePages = () => {
        rowsClicked.map(row => {
            templates.filter((template, index) => {
                if (template.id === row) {
                    if (template.tenantId === null) {
                        Toast.error(
                            "'" +
                                template.name +
                                "' is a System Template. System Templates cannot be deleted within a Client.",
                        );
                        return;
                    }
                    if (template.templateStatus !== -1) {
                        deletePortalPage(template.id).then(response => {
                            Toast.success(
                                "Template '" +
                                    template.name +
                                    "' deleted successfully.",
                            );
                            deselectRows(rowsClicked);
                            loadTemplates();
                        });
                    } else {
                        Toast.error(
                            "'" +
                                template.name +
                                "' is a System Template. System Templates cannot be deleted within a Client.",
                        );
                    }
                }
                return null;
            });
            return null;
        });
    };

    const deselectRows = rowsClicked => {
        rowsClicked.map(row => {
            templates.filter(template => {
                if (template.id === row) {
                    document.getElementById(template.id).checked = false;
                }
                return null;
            });
            return null;
        });

        setRowsClicked([]);
    };

    const duplicatePages = async () => {
        if (!rowsClicked || rowsClicked.length === 0) {
            Toast.error(getText('msg_d5b78eba52c2'));
            return;
        }

        const createdTemplates = [];
        const failedTemplates = [];
        const existingNames = templates.map(t => t.name).filter(Boolean);
        let currentClientAgencyId = null;

        try {
            const currentClient = await getClient();
            currentClientAgencyId = currentClient?.agencyId ?? null;
        } catch (error) {
            Toast.error(getText('msg_346f5e27d48b'));
            return;
        }

        for (let index = 0; index < rowsClicked.length; index += 1) {
            const templateId = rowsClicked[index];

            try {
                const selectedTemplate = templates.find(
                    t => t.id === templateId,
                );
                const tenantIdForRead = selectedTemplate?.tenantId ?? 0;
                const original = await readPortalPage(
                    templateId,
                    true,
                    tenantIdForRead,
                );
                const originalTemplate = original?.viewModels?.[0];

                if (!originalTemplate) {
                    throw new Error(`Template not found for id ${templateId}`);
                }

                const { id: omittedId, ...payload } = originalTemplate;
                void omittedId;

                payload.templateScope = ClientTemplateScope;

                if (currentClientAgencyId !== null) {
                    payload.agencyId = currentClientAgencyId;
                }

                if ('tenantId' in payload) {
                    payload.tenantId = localStorage.getItem('tenantId');
                }

                payload.name =
                    typeof getNameForDuplicate === 'function'
                        ? getNameForDuplicate(
                              originalTemplate.name,
                              existingNames,
                          )
                        : `${originalTemplate.name} (Copy)`;

                const createdResponse = await createPortalPage(payload);
                const createdTemplate =
                    createdResponse?.viewModels?.[0] || createdResponse;

                if (!createdTemplate || createdTemplate.id == null) {
                    throw new Error('Unexpected created template response');
                }

                if (
                    createdTemplate.agencyId === null ||
                    createdTemplate.agencyId === undefined
                ) {
                    createdTemplate.agencyId = currentClientAgencyId;
                }

                createdTemplates.push(createdTemplate);
                existingNames.push(payload.name);
            } catch (error) {
                const templateName =
                    templates.find(t => t.id === templateId)?.name ||
                    String(templateId);
                failedTemplates.push(templateName);
            }
        }

        if (createdTemplates.length > 0) {
            setTemplates(prevTemplates => [
                ...createdTemplates,
                ...prevTemplates,
            ]);
        }

        if (failedTemplates.length === 0) {
            Toast.success(
                `${createdTemplates.length > 1 ? 'Templates' : 'Template'} duplicated successfully.`,
            );
            setRowsClicked([]);
            return;
        }

        if (createdTemplates.length > 0) {
            Toast.success(
                `${createdTemplates.length} ${createdTemplates.length > 1 ? 'templates' : 'template'} duplicated.`,
            );
        }

        Toast.error(`Failed to duplicate: ${failedTemplates.join(', ')}.`);
    };

    const rowAction = action => {
        if (rowsClicked.length > 0) {
            switch (action) {
                case 'delete':
                    showModal({
                        type: ModalTypes.DELETE_CONFIRMATION,
                        title: getText('msg_91bdc8be8760'),
                        text: getText('msg_3a38177bb850'),
                        onYes: () => deletePages(),
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_f6fdbe48dc54'),
                    });
                    break;
                case 'duplicate':
                    showModal({
                        type: ModalTypes.ACTION,
                        title: getText('msg_5ebb09c8ea76'),
                        subtitle: '',
                        text: `This action will create copies of the ${rowsClicked.length > 1 ? 'selected templates' : 'selected template'}.`,
                        onYes: () => duplicatePages(),
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_972d57379db3'),
                    });
                    break;
                default:
                    break;
            }
        } else {
            Toast.error(
                getText('msg_9bcf67dc54d5'),
            );
        }
    };

    return isLoading ? (
        <Spinner width={50} className="pageListloadingSpinner" />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                            className="mb-4"
                        >
                            <p className="page-title float-start card-title ms-4">
                                {getText('msg_f25b700ed9f0')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_f00cd49d3a37')}
                            </p>
                        </div>
                        {!hasAttribute(
                            SecurityAttributes.TemplatesReadOnly,
                        ) && (
                            <div className="float-end d-flex align-items-center">
                                <span
                                    type="button"
                                    onClick={() => rowAction('delete')}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                                <span
                                    type="button"
                                    onClick={() => rowAction('duplicate')}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-copy me-2" />
                                    {getText('msg_972d57379db3')}
                                </span>
                                <span
                                    onClick={() =>
                                        navigate('new-template', {
                                            state: {
                                                loadFromClientTemplates: true,
                                            },
                                        })
                                    }
                                    type="button"
                                    className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="text-center">
                                        <svg
                                            width="19"
                                            height="19"
                                            viewBox="0 0 19 19"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.2 9.3H12.8"
                                                stroke="#0070FF"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="17"
                                                height="17"
                                                rx="4"
                                                stroke="#0070FF"
                                            />
                                        </svg>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        <span
                                            onClick={handleClickName}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_55f8ebc805e6')}
                                        </span>
                                        <span
                                            onClick={handleClickContentType}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'type' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_4651a34e4df9')}
                                        </span>
                                        <span
                                            onClick={handleClickScope}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'templateScope' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTemplates.map(template => (
                                    <tr
                                        key={template.id}
                                        className={`${rowsClicked.includes(template.id) ? 'clicked-class' : ''}`}
                                    >
                                        <td className="text-center">
                                            <input
                                                id={template.id}
                                                onClick={() =>
                                                    handlerRowClicked(
                                                        template.id,
                                                    )
                                                }
                                                className="form-check-input"
                                                type="checkbox"
                                            />
                                        </td>
                                        <td
                                            className="item-page-name titleCell"
                                            onClick={() =>
                                                navigate(
                                                    `/portal-templates/preview/${template.id}`,
                                                    {
                                                        state: {
                                                            template,
                                                        },
                                                    },
                                                )
                                            }
                                        >
                                            {template.name}
                                        </td>
                                        <td className="subtitleCell">
                                            {template.description}
                                        </td>
                                        <td className="subtitleCell">
                                            {getScopeLabel(template)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p
                        className="text-center fs-5"
                        style={{
                            margin: '60px 0',
                            color: 'rgba(47, 43, 67, 0.6)',
                        }}
                    >
                        {templates.length <= 0 ? 'No templates found' : ''}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PortalTemplatesList;
